import { crush } from 'radash';
import React, { useState, useEffect } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Tooltip } from 'react-tooltip';
import { unparse } from 'papaparse';
import { IoMdDownload } from 'react-icons/io';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import { FaExclamationCircle, FaCheck } from 'react-icons/fa';
import { ImBlocked } from 'react-icons/im';
import { useGlobal, useItems } from '../../state';
import { Button, Check } from '../input';
import { Modal } from '../modal';
import { Copy } from '../copy';
import { Loading } from '../loading';
import { styles } from '../../constants';

export const resultBoxStyle = {
  padding: 10,
  background: '#f6f6f6',
  border: '1px solid #eee',
  borderRadius: 5,
};

export const resultHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 5,
  fontSize: 14,
};

const padding = 4;

const modal = (item) => (
  <Modal
    title={
      <div>
        <b>Details</b>
      </div>
    }
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, width: 20 }}>
      <FaMagnifyingGlass />
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <OneItemTable item={item} />
    </div>
  </Modal>
);

const renderValue = (key, val, idx) => {
  if (key == '_status') {
    const body =
      {
        ok: <FaCheck size={12} />,
        block: <ImBlocked size={14} />,
        error: <FaExclamationCircle size={14} />,
      }[val] || val;

    const ttid = `status-${idx}`;

    return (
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        data-tooltip-id={ttid}
        data-tooltip-content={val}
      >
        {body}
        <Tooltip place="bottom" id={ttid} />
      </div>
    );
  }

  if (typeof val == 'string' && val.startsWith('http')) {
    return (
      <a href={val} target="_blank" rel="noopener noreferrer">
        {val}
      </a>
    );
    // } else if (Array.isArray(val)) {
    //   return (
    //     <ul style={{ margin: 0, padding: 0, paddingLeft: 15 }}>
    //       {val.map((it) => (
    //         <li>{renderValue(it)}</li>
    //       ))}
    //     </ul>
    //   );
  } else if (typeof val == 'string') {
    return val;
  } else if (val === null) {
    return '';
  } else {
    return JSON.stringify(val);
  }
};

const ManyItemsTable = ({ table, items }) => (
  <table style={{ fontSize: 12, tableLayout: 'auto', width: 'max-content', marginx: '10px 0' }}>
    <thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id}>
          <th></th>
          {headerGroup.headers.map((header) => (
            <th style={{ padding }} key={header.id}>
              {flexRender(header.column.columnDef.header, header.getContext())}
            </th>
          ))}
        </tr>
      ))}
    </thead>
    <tbody>
      {table.getRowModel().rows.map((row, idx) => (
        <tr key={row.id}>
          <td style={{}}>{modal(items[idx])}</td>
          {row.getVisibleCells().map((cell) => (
            <td
              key={cell.id}
              style={{
                whiteSpace: 'nowrap',
                maxWidth: 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                padding,
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const OneItemTable = ({ item }) => {
  return (
    <table style={{ fontSize: 12, tableLayout: 'auto', width: 'max-content', marginx: '10px 0' }}>
      <tbody>
        {Object.keys(item).map((key) => (
          <tr key={key}>
            <td
              style={{
                padding,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                verticalAlign: 'top',
              }}
            >
              {key}
            </td>
            <td
              style={{
                padding,
                maxWidth: 800,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {flexRender(renderValue(key, item[key]))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const Pager = ({ table, total }) => {
  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();

  const start = pageIndex * pageSize + 1;
  const end = Math.min(total, (pageIndex + 1) * pageSize);

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
      <Button small grayFlat onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
        {'<<'}
      </Button>

      <Button small grayFlat onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
        {'<'}
      </Button>

      <div>
        Page {pageIndex + 1} of {pageCount}
      </div>

      <Button small grayFlat onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
        {'>'}
      </Button>

      <Button small grayFlat onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}>
        {'>>'}
      </Button>

      <div style={{ marginLeft: 10 }}>
        Rows {total === 0 ? 0 : start}-{end} of {total}
      </div>
    </div>
  );
};

export const ItemsTable = ({ jobId, job }) => {
  const limit = 100;

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: limit,
  });

  const offset = pagination.pageIndex * limit;

  const it = useItems(jobId, limit, offset);
  const { host, apiKey } = useGlobal();

  const meta = it?.items?.meta || {};
  const total = meta.total || 0;

  const items = it?.items?.results || [];
  const hasItems = items.length > 0;

  const pageCount = Math.max(1, Math.ceil(total / limit));

  const [showHidden, setShowHidden] = useState(false);
  const columnHelper = createColumnHelper();

  const download = async (url, filename) => {
    const resp = await fetch(url, {
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
    });
    const blob = await resp.blob();

    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;

    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(href);
  };

  const handleCsv = () => {
    download(`${host}/api/jobs/${jobId}/items/csv`, 'items.csv');
  };

  const handleJsonl = () => {
    download(`${host}/api/jobs/${jobId}/items/jsonl`, 'items.jsonl');
  };

  const columns = React.useMemo(() => {
    if (!hasItems) return [];

    const allKeys = { _status: true };

    for (const item of items.slice(0, 100)) {
      for (const key of Object.keys(item)) {
        allKeys[key] = true;
      }
    }

    const copyNode = (key) => {
      if (key === '_status') return;

      const copyValue = items
        .map((it) => it[key])
        .map((it) => (typeof it === 'string' ? it : JSON.stringify(it)))
        .join('\n')
        .trim();

      return <Copy value={copyValue} onlyCheck />;
    };

    const pretty = (k) => (k === '_status' ? '' : k);
    let idx = 1;

    return Object.keys(allKeys)
      .filter((key) => showHidden || !key.startsWith('_') || key === '_status')
      .map((key) =>
        columnHelper.accessor(key, {
          header: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>{pretty(key)}</div>
              <div>{copyNode(key)}</div>
            </div>
          ),
          cell: (info) => {
            const val = info.getValue();
            return renderValue(key, val, idx++);
          },
        }),
      );
  }, [items, showHidden]);

  const table = useReactTable({
    data: items,
    columns,
    state: { pagination },
    onPaginationChange: setPagination,
    manualPagination: true,
    pageCount,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
  });

  // return (
  // );

  const controlsNode = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <Check useSwitch size="sm" value={showHidden} onChange={setShowHidden}>
        Show hidden fields
      </Check>

      <Button small grayFlat onClick={handleCsv}>
        <IoMdDownload />
        CSV
      </Button>
      <Button small grayFlat onClick={handleJsonl}>
        <IoMdDownload />
        JSONL
      </Button>
    </div>
  );

  if (it.loading && !hasItems) {
    return (
      <div>
        <div>{/*JSON:{JSON.stringify(it.items)}*/}</div>
        <Loading />
      </div>
    );
  }

  return (
    <div>
      <div style={styles.stickyHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div>
            <b>{total}</b> {total === 1 ? 'item' : 'items'}
          </div>

          {job && job?.state === 'active' && <Loading size="xs" />}
        </div>

        {hasItems && controlsNode}
      </div>

      <div style={{ overflowX: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {total > limit && <Pager table={table} total={total} />}

        <div style={{ opacity: it.loading ? '50%' : '100%' }}>
          {items.length === 1 && <OneItemTable item={items[0]} />}
          {items.length !== 1 && <ManyItemsTable table={table} items={items} />}
        </div>
        {total > limit && <Pager table={table} total={total} />}
      </div>
    </div>
  );
};
